import * as React from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';

import { triggerVideo } from '../reducers/tutorial-videos';

import {
  AGENCY_HOME_REGEXP,
  EDIT_OFFER_REGEXP,
  MESSAGES_REGEXP,
  OFFERS_REGEXP,
  PARTICIPANT_REGEXP,
  SETTINGS_REGEXP,
  SPECIFY_OFFER_REGEXP,
  VideoKind
} from '../util/videos';

import type { LocationWindow } from 'types';

type OwnProps = {
  location: LocationWindow,
  children: React.ReactNode | undefined | null
};

type Props = {
  dispatch: any
} & OwnProps;

class RouterTutorialHandler extends React.PureComponent<Props> {
  inactiveTriggerTimeout?: number;
  lastOfferEdit?: number;
  path?: string;
  debouncedTrigger: (a: string) => void;

  constructor(props: Props) {
    super(props);

    this.debouncedTrigger = debounce(this.triggerOnLocationUpdate, 1000);
    this.setInactiveTrigger();
  }

  componentDidMount(): void {
    const { location } = this.props;
    this.debouncedTrigger(location.path || '');
  }

  componentDidUpdate(prevProps: Props): void {
    const { location } = this.props;
    const hasLocationUpdate = prevProps.location.path !== location.path;

    if (hasLocationUpdate) {
      this.debouncedTrigger(location.path || '');
    }
  }

  componentWillUnmount(): void {
    this.unsetInactiveTrigger();
  }

  resetInactiveTimeout = (): void => {
    clearTimeout(this.inactiveTriggerTimeout);
    this.inactiveTriggerTimeout = window.setTimeout(this.triggerOnInactivity, 10000);
  };

  unsetInactiveTrigger(): void {
    window.removeEventListener('click', this.resetInactiveTimeout);
    window.removeEventListener('keydown', this.resetInactiveTimeout);
    clearTimeout(this.inactiveTriggerTimeout);
  }

  setInactiveTrigger(): void {
    this.unsetInactiveTrigger();
    window.addEventListener('click', this.resetInactiveTimeout);
    window.addEventListener('keydown', this.resetInactiveTimeout);
    this.resetInactiveTimeout();
  }

  triggerOnInactivity = (): void => {
    const path = this.path;
    if (!path) {
      return;
    }

    const { dispatch } = this.props;
    // 1. Create offer, inactive for 10 seconds
    if (OFFERS_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.CREATE_OFFER));
    }

    // 2. Edit an existing offer, inactive for 10 seconds
    if (PARTICIPANT_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.EDIT_OFFER));
    }

    // 3. Bulk shortlist and bulk offer, inactive for 10 seconds
    if (OFFERS_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.BULK_SHORTLIST_BULK_OFFER));
    }

    // 4. Bulk message and bulk edit, inactive for 10 seconds
    if (MESSAGES_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.BULK_MESSAGE_BULK_OFFER));
    }

    // 4. Manage multiple brands - overview, inactive for 10 seconds
    if (AGENCY_HOME_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.BULK_MESSAGE_BULK_OFFER));
    }

    // 5. Manage multiple brands - overview, inactive for 10 seconds
    if (AGENCY_HOME_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.MANAGE_MULTI_BRAND));
    }

    this.unsetInactiveTrigger();
  };

  triggerOnLocationUpdate(path: string = '') {
    this.path = path;
    const { dispatch } = this.props;

    this.setInactiveTrigger();

    // 1. Create your offer, 1st click on offers tab
    if (SPECIFY_OFFER_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.CREATE_OFFER));
    }

    // 2. Edit an existing offer, 1st click on offers tab
    if (EDIT_OFFER_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.EDIT_OFFER));
    }

    // 3. Bulk shortlist and bulk offer, 1st click on offers tab
    if (OFFERS_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.BULK_SHORTLIST_BULK_OFFER));
    }

    // 4. Bulk message and bulk edit, 1st click on messages
    if (MESSAGES_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.BULK_MESSAGE_BULK_OFFER));
    }

    // 4. Bulk message and bulk edit, more than 2 edits in 5 min
    if (EDIT_OFFER_REGEXP.some((regex) => regex.test(path))) {
      if (this.lastOfferEdit) {
        const diffInMinutes = (Date.now() - this.lastOfferEdit) / 1000 / 60;
        if (diffInMinutes < 5) {
          dispatch(triggerVideo(VideoKind.BULK_MESSAGE_BULK_OFFER));
        }
      }

      this.lastOfferEdit = Date.now();
    }

    // 5. Manage multiple brands - overview, 1st click on Home
    if (AGENCY_HOME_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.MANAGE_MULTI_BRAND));
    }

    // 7. Manage your email settings, 1st click on My Account
    if (SETTINGS_REGEXP.some((regex) => regex.test(path))) {
      dispatch(triggerVideo(VideoKind.MANAGE_SETTINGS));
    }
  }

  render(): React.ReactNode {
    const { children } = this.props;
    return children || null;
  }
}

export default connect()(RouterTutorialHandler);
